import { LoadingButton } from '@mui/lab'
import {
  Dialog,
  DialogContent,
  Grid,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material'
import { ExplanationAccordion } from 'components'
import { vehicleCheckInManufacturerLabelDatesValidationSchema, VehicleIncorrectMileageResolutionDto } from 'dtos'
import { Formik } from 'formik'

interface ClusterIncorrectMileageResolutionEditDialogProps {
  initialValues: VehicleIncorrectMileageResolutionDto
  open: boolean
  onClose: () => void
  onSave: (values: VehicleIncorrectMileageResolutionDto) => void
  isLoading: boolean
}

export default function ClusterIncorrectMileageResolutionEditDialog({
  open,
  onClose,
  onSave,
  isLoading,
  initialValues
}: ClusterIncorrectMileageResolutionEditDialogProps) {
  return (
    <Dialog open={open} onClose={onClose}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={vehicleCheckInManufacturerLabelDatesValidationSchema}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          const noIssueSelected = !values.incorrectMileageResolutionIssue
          const isDiscrepancy = values.incorrectMileageResolutionIssue === 'odometerDiscrepancy'
          const isIncorrectCheckIn = values.incorrectMileageResolutionIssue === 'incorrectOdometerReadingDuringVehicleCheckIn'

          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Incorrect Mileage Resolution</DialogTitle>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant='h6'>Issue</Typography>
                </Grid>

                <Grid item xs={12} sm={12}>
                  <RadioGroup
                    name='incorrectMileageResolutionIssue'
                    onChange={handleChange}
                    row
                    value={values.incorrectMileageResolutionIssue}
                  >
                    <FormControlLabel
                      value='odometerDiscrepancy'
                      control={<Radio disabled={isLoading || isSubmitting} />}
                      label='Odometer Discrepancy'
                    />
                    <FormControlLabel
                      value='incorrectOdometerReadingDuringVehicleCheckIn'
                      control={<Radio disabled={isLoading || isSubmitting} />}
                      label='Incorrect Odometer Reading During Vehicle Check-In'
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label='Odometer Reading'
                    name='incorrectMileageResolutionOdometerReadingMileage'
                    type='number'
                    value={values.incorrectMileageResolutionOdometerReadingMileage}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isLoading || isSubmitting || noIssueSelected || isDiscrepancy}
                    error={
                      touched.incorrectMileageResolutionOdometerReadingMileage &&
                      Boolean(errors.incorrectMileageResolutionOdometerReadingMileage)
                    }
                    helperText={
                      touched.incorrectMileageResolutionOdometerReadingMileage &&
                      typeof errors.incorrectMileageResolutionOdometerReadingMileage === 'string'
                        ? errors.incorrectMileageResolutionOdometerReadingMileage
                        : undefined
                    }
                  />
                </Grid>

                <Grid item xs={6} sm={6}>
                  <RadioGroup
                    typeof='string'
                    name='incorrectMileageResolutionOdometerReadingUnitOfMeasurement'
                    onChange={handleChange}
                    row
                    value={values.incorrectMileageResolutionOdometerReadingUnitOfMeasurement}
                  >
                    <FormControlLabel
                      value='Kilometers'
                      control={<Radio disabled={isLoading || isSubmitting || noIssueSelected || isDiscrepancy} />}
                      label='Kilometers'
                    />
                    <FormControlLabel
                      value='Miles'
                      control={<Radio disabled={isLoading || isSubmitting || noIssueSelected || isDiscrepancy} />}
                      label='Miles'
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading || isSubmitting || noIssueSelected || isIncorrectCheckIn}
                    error={Boolean(touched.incorrectMileageResolutionNotes && errors.incorrectMileageResolutionNotes)}
                    fullWidth
                    helperText={
                      touched.incorrectMileageResolutionNotes && typeof errors.incorrectMileageResolutionNotes === 'string'
                        ? errors.incorrectMileageResolutionNotes
                        : undefined
                    }
                    label='Notes'
                    maxRows={4}
                    minRows={4}
                    multiline
                    name='incorrectMileageResolutionNotes'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.incorrectMileageResolutionNotes || ''}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <ExplanationAccordion>
                    || If user selects “Odometer Discrepancy”, the “Notes” field becomes enabled and required to Save. With
                    “Odometer Discrepancy” selected, pressing “Save” will add the entered Note to the “Importer Notes” tab for
                    this vehicle, add a date/time stamp and the user’s first and last name to the Incorrect Mileage Resolved
                    field on the Incorrect Mileage Resolution card, and close the dialog. || If user selects “Incorrect Odometer
                    Reading During Vehicle Check-In”, the “Odometer Reading” input field and Unit of Measure radio buttons
                    (Kilometers/Miles) become enabled and required to Save. With “Incorrect Odometer Reading During Vehicle
                    Check-In” selected, pressing “Save” will update the “Vehicle Check-In Odometer Reading” field on the
                    “Cluster Information” card for this vehicle, add a date/time stamp and the user’s first and last name to the
                    Incorrect Mileage Resolved field on the Incorrect Mileage Resolution card, and close the dialog. Note that
                    the “Odometer Reading” field on the Vehicle Details card under the Vehicle Check-In tab for this vehicle is
                    NOT updated by this process. ||
                  </ExplanationAccordion>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button color='inherit' onClick={onClose} disabled={isSubmitting || isLoading} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton color='primary' type='submit' loading={isSubmitting || isLoading} variant='contained'>
                  Save
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
